import React, { useState, useEffect, useMemo } from 'react';
import VisibilityBadges from './VisibilityBadges';
import LocationFilter from './LocationFilter';

export default function ProductCatalog({ locationId, locationName }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState(locationId || '');
  const [menuFilter, setMenuFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [modifierFilter, setModifierFilter] = useState('');
  const [modifierGroupFilter, setModifierGroupFilter] = useState('');
  const [comboFilter, setComboFilter] = useState('');
  const [taxFilter, setTaxFilter] = useState('');

  useEffect(() => {
    setLocationFilter(locationId || '');
  }, [locationId]);

  useEffect(() => {
    fetchProducts();
  }, [locationFilter]);

  async function fetchProducts() {
    try {
      setLoading(true);
      setError(null);
      const query = locationFilter ? `?locationId=${locationFilter}` : '';
      const response = await fetch(`/api/products/all-with-thumbnails${query}`);
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setProducts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchProductDetails(productGuid) {
    try {
      const response = await fetch(`/api/products/${productGuid}`);
      if (!response.ok) throw new Error('Failed to fetch product details');
      const data = await response.json();
      setSelectedProduct(data);
    } catch (err) {
      console.error('Error fetching product details:', err);
    }
  }

  const menuOptions = useMemo(
    () => [...new Set(products.map(p => p.menu_name).filter(Boolean))].sort(),
    [products]
  );

  const groupOptions = useMemo(
    () => [...new Set(
      products
        .filter(p => !menuFilter || p.menu_name === menuFilter)
        .map(p => p.menu_group_name)
        .filter(Boolean)
    )].sort(),
    [products, menuFilter]
  );

  const modifierGroupOptions = useMemo(
    () => [...new Set(
      products.flatMap(p => (p.modifier_group_names || '').split(',').map(name => name.trim()).filter(Boolean))
    )].sort(),
    [products]
  );

  function handleMenuFilterChange(value) {
    setMenuFilter(value);
    setGroupFilter('');
  }

  function clearAllFilters() {
    setMenuFilter('');
    setGroupFilter('');
    setModifierFilter('');
    setModifierGroupFilter('');
    setComboFilter('');
    setTaxFilter('');
  }

  const filteredProducts = products.filter(p =>
    (!menuFilter || p.menu_name === menuFilter) &&
    (!groupFilter || p.menu_group_name === groupFilter) &&
    (!modifierFilter || (modifierFilter === 'yes' ? p.modifier_count > 0 : p.modifier_count === 0)) &&
    (!modifierGroupFilter || (p.modifier_group_names || '').split(',').map(n => n.trim()).includes(modifierGroupFilter)) &&
    (!comboFilter || (comboFilter === 'yes' ? !!p.is_combo_parent : !p.is_combo_parent)) &&
    (!taxFilter || (taxFilter === 'yes' ? p.tax_count > 0 : p.tax_count === 0)) &&
    (p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.menu_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.menu_group_name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const hasActiveFilters = menuFilter || groupFilter || modifierFilter || modifierGroupFilter || comboFilter || taxFilter;

  return (
    <div className="product-catalog-container">
      <div className="product-list-section">
        <h2>📦 Product Catalog</h2>

        <div className="search-box">
          <input
            type="text"
            placeholder="Search products, menus, or categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="catalog-filters">
          <LocationFilter value={locationFilter} onChange={setLocationFilter} />
          <div className="filter-group">
            <label>Menu</label>
            <select value={menuFilter} onChange={(e) => handleMenuFilterChange(e.target.value)}>
              <option value="">All Menus</option>
              {menuOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Menu Group</label>
            <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
              <option value="">All Groups</option>
              {groupOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Modifiers</label>
            <select value={modifierFilter} onChange={(e) => setModifierFilter(e.target.value)}>
              <option value="">All Products</option>
              <option value="yes">Has Modifiers</option>
              <option value="no">No Modifiers</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Modifier Group</label>
            <select value={modifierGroupFilter} onChange={(e) => setModifierGroupFilter(e.target.value)}>
              <option value="">All Modifier Groups</option>
              {modifierGroupOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Combo</label>
            <select value={comboFilter} onChange={(e) => setComboFilter(e.target.value)}>
              <option value="">All Products</option>
              <option value="yes">Combo Items Only</option>
              <option value="no">Non-Combo Only</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Tax</label>
            <select value={taxFilter} onChange={(e) => setTaxFilter(e.target.value)}>
              <option value="">All Products</option>
              <option value="yes">Taxed</option>
              <option value="no">Non-Taxed</option>
            </select>
          </div>
          {hasActiveFilters && (
            <button className="btn-secondary" onClick={clearAllFilters}>Clear filters</button>
          )}
        </div>

        {loading && <p className="loading">Loading products...</p>}
        {error && <div className="error">✕ {error}</div>}

        <p className="results-count">{filteredProducts.length} of {products.length} products</p>

        <div className="products-grid">
          {filteredProducts.map(product => (
            <div
              key={product.guid}
              className="product-card"
              onClick={() => fetchProductDetails(product.guid)}
            >
              {product.image_url && (
                <div className="product-image">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/200?text=No+Image';
                    }}
                  />
                </div>
              )}
              <div className="product-card-content">
                <h3>{product.name}</h3>
                {!locationFilter && product.location_name && (
                  <p className="location-cell">{product.location_name}</p>
                )}
                <p className="menu-breadcrumb">
                  {product.menu_name} / {product.menu_group_name}
                </p>
                {product.price > 0 && (
                  <p className="product-price">${product.price.toFixed(2)}</p>
                )}
                {(!!product.modifier_count || !!product.is_combo_parent || !!product.tax_count) && (
                  <div className="card-indicators">
                    {!!product.modifier_count && (
                      <span className="card-indicator" title={`${product.modifier_count} modifier group(s)`}>🔧 {product.modifier_count}</span>
                    )}
                    {!!product.is_combo_parent && (
                      <span className="card-indicator" title="Combo item">🍱 Combo</span>
                    )}
                    {!!product.tax_count && (
                      <span className="card-indicator" title={`${product.tax_count} tax rate(s) applied`}>🧾 Taxed</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedProduct && (
        <ProductDetailsPanel product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </div>
  );
}

function ProductDetailsPanel({ product, onClose }) {
  const { product: productData, location, menuItem, menu, menuGroup, pricingRules, taxes, allergens, images, modifierGroups } = product;

  return (
    <div className="product-details-panel">
      <button className="close-btn" onClick={onClose}>✕</button>

      <div className="details-content">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          {location && <span className="location-cell" title={location.guid}>{location.name}</span>}
          {location && (menu || menuGroup) && <span className="separator">/</span>}
          {menu && <span>{menu.name}</span>}
          {menuGroup && <span className="separator">/</span>}
          {menuGroup && <span>{menuGroup.name}</span>}
        </div>

        {/* Image Gallery */}
        {images && images.length > 0 && (
          <div className="image-section">
            <img
              src={images[0].image_url}
              alt={productData.name}
              className="detail-image"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/400?text=Image+Not+Found';
              }}
            />
            {images.length > 1 && (
              <div className="image-count">+{images.length - 1} more images</div>
            )}
          </div>
        )}

        {/* Product Header */}
        <h1>{productData.name}</h1>
        {productData.pos_name && <p className="pos-name">POS Name: {productData.pos_name}</p>}
        {productData.description && <p className="description">{productData.description}</p>}

        {/* Pricing Section */}
        <div className="details-section">
          <h3>💰 Pricing</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Price:</label>
              <span className="price">${(productData.price || 0).toFixed(2)}</span>
            </div>
            <div className="info-item">
              <label>Base Price:</label>
              <span>${(productData.base_price || 0).toFixed(2)}</span>
            </div>
            <div className="info-item">
              <label>Strategy:</label>
              <span>{productData.pricing_strategy || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Tax Inclusion:</label>
              <span>{productData.tax_inclusion || 'N/A'}</span>
            </div>
          </div>

          {pricingRules && pricingRules.length > 0 && (
            <div className="pricing-rules">
              <h4>Time-Specific Pricing:</h4>
              {pricingRules.map((rule, idx) => (
                <div key={idx} className="pricing-rule">
                  <div className="rule-header">
                    Base: ${rule.base_price} → Peak: ${rule.time_specific_price}
                  </div>
                  <div className="rule-details">
                    <span>{JSON.parse(rule.schedule_days || '[]').join(', ')}</span>
                    <span>{rule.time_range_start} - {rule.time_range_end}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Visibility Section */}
        <div className="details-section">
          <h3>👁️ Visibility</h3>
          <VisibilityBadges visibility={productData.visibility} />
        </div>

        {/* Taxes Section */}
        {taxes && taxes.length > 0 && (
          <div className="details-section">
            <h3>🧾 Taxes</h3>
            <div className="taxes-list">
              {taxes.map((tax, idx) => (
                <div key={idx} className="tax-item">
                  <span className="tax-name">{tax.tax_name || 'Unknown Tax'}</span>
                  {tax.tax_percentage && <span className="tax-rate">{tax.tax_percentage}%</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Allergens Section */}
        {allergens && allergens.length > 0 && (
          <div className="details-section">
            <h3>⚠️ Allergens</h3>
            <div className="allergens-list">
              {allergens.map((allergen, idx) => (
                <span key={idx} className="allergen-badge">{allergen}</span>
              ))}
            </div>
          </div>
        )}

        {/* Modifier Groups Section */}
        {modifierGroups && modifierGroups.length > 0 && (
          <div className="details-section">
            <h3>🔧 Options/Modifiers</h3>
            {modifierGroups.map((group, idx) => (
              <div key={idx} className="modifier-group">
                <h4>{group.name}</h4>
                <p className="modifier-range">
                  Select {group.min_selected || 0}-{group.max_selected || 1}
                </p>
                <ul className="modifiers-list">
                  {group.modifiers && group.modifiers.map((mod, midx) => (
                    <li key={midx}>
                      <span>{mod.name}</span>
                      {mod.price_adjustment && (
                        <span className="mod-price">
                          {mod.price_adjustment > 0 ? '+' : ''} ${mod.price_adjustment.toFixed(2)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Additional Details */}
        <div className="details-section">
          <h3>📋 Details</h3>
          <div className="info-grid">
            {productData.sales_category_name && (
              <div className="info-item">
                <label>Category:</label>
                <span>{productData.sales_category_name}</span>
              </div>
            )}
            {productData.calories && (
              <div className="info-item">
                <label>Calories:</label>
                <span>{productData.calories}</span>
              </div>
            )}
            {productData.sku && (
              <div className="info-item">
                <label>SKU:</label>
                <span>{productData.sku}</span>
              </div>
            )}
            {productData.plu && (
              <div className="info-item">
                <label>PLU:</label>
                <span>{productData.plu}</span>
              </div>
            )}
            {productData.kitchen_name && (
              <div className="info-item">
                <label>Kitchen:</label>
                <span>{productData.kitchen_name}</span>
              </div>
            )}
            {productData.prep_time && (
              <div className="info-item">
                <label>Prep Time:</label>
                <span>{productData.prep_time} min</span>
              </div>
            )}
            {productData.unit_of_measure && productData.unit_of_measure !== 'NONE' && (
              <div className="info-item">
                <label>Unit of Measure:</label>
                <span>{productData.unit_of_measure}</span>
              </div>
            )}
            {productData.weight && (
              <div className="info-item">
                <label>Weight:</label>
                <span>{productData.weight} {productData.weight_unit}</span>
              </div>
            )}
            {(productData.length || productData.width || productData.height) && (
              <div className="info-item">
                <label>Dimensions (L×W×H):</label>
                <span>
                  {productData.length || '-'} × {productData.width || '-'} × {productData.height || '-'} {productData.dimension_unit}
                </span>
              </div>
            )}
            {!!productData.guest_count && (
              <div className="info-item">
                <label>Guest Count:</label>
                <span>{productData.guest_count}</span>
              </div>
            )}
            <div className="info-item">
              <label>Discountable:</label>
              <span>{productData.is_discountable ? '✓ Yes' : '✗ No'}</span>
            </div>
            <div className="info-item">
              <label>Deferred:</label>
              <span>{productData.is_deferred ? '✓ Yes' : '✗ No'}</span>
            </div>
            <div className="info-item">
              <label>Combo Parent:</label>
              <span>{productData.is_combo_parent ? '✓ Yes' : '✗ No'}</span>
            </div>
            <div className="info-item">
              <label>Contains Alcohol:</label>
              <span>{productData.contains_alcohol ? '✓ Yes' : '✗ No'}</span>
            </div>
            {(productData.pos_button_color_light || productData.pos_button_color_dark) && (
              <div className="info-item">
                <label>POS Button Colors:</label>
                <span className="pos-color-swatches">
                  {productData.pos_button_color_light && (
                    <span
                      className="color-swatch"
                      style={{ backgroundColor: productData.pos_button_color_light }}
                      title={`Light: ${productData.pos_button_color_light}`}
                    />
                  )}
                  {productData.pos_button_color_dark && (
                    <span
                      className="color-swatch"
                      style={{ backgroundColor: productData.pos_button_color_dark }}
                      title={`Dark: ${productData.pos_button_color_dark}`}
                    />
                  )}
                </span>
              </div>
            )}
            {productData.created_at && (
              <div className="info-item">
                <label>Created:</label>
                <span>{new Date(productData.created_at).toLocaleString()}</span>
              </div>
            )}
            {productData.synced_at && (
              <div className="info-item">
                <label>Updated:</label>
                <span>{new Date(productData.synced_at).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Images Gallery */}
        {images && images.length > 1 && (
          <div className="details-section">
            <h3>🖼️ All Images</h3>
            <div className="images-gallery">
              {images.map((img, idx) => (
                <div key={idx} className="gallery-image">
                  <img
                    src={img.image_url}
                    alt={`Product ${idx + 1}`}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/150?text=Image+Error';
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
